/**
 * Account Summary API
 * URL: https://developer.syf.com/api-reference/account-summary-v2-v1#operation/getAccountSummary
 */

import { StatementEnrollment } from 'typings/statement'

export interface AccountSummaryError {
  code: string
  message: string
  trackingId: string
  transient: boolean
}

export enum AccountSummaryConditions {
  Open = 'OPEN',
  Shell = 'SHELL',
  Closed = 'CLOSED',
  AuthProhibited = 'AUTH_PROHIBITED',
  ClosedWithBalance = 'CLOSED_WITH_BALANCE',
  ChargedOff = 'CHARGED_OFF',
  Delinquent = 'DELINQUENT',
  OverLimit = 'OVERLIMIT',
  CreditBalance = 'CREDIT_BALANCE',
  CreditLineIncreaseEligible = 'CREDIT_LINE_INCREASE_ELIGIBLE'
}

export enum ProductTypes {
  dualCard = 'DUAL_CARD',
  limitedUseDualCard = 'LIMITED_USE_DUAL_CARD',
  privateLabel = 'PRIVATE_LABEL',
  installmentLoan = 'INSTALLMENT_LOAN',
  businessRetailCard = 'BUSINESS_RETAIL_CARD'
}

export enum LineOfBusiness {
  retailCard = 'RETAIL_CARD',
  PaymentSolutionsCareCredit = 'PAYMENT_SOLUTIONS_CARECREDIT'
}

export enum AnnualPercentageRateType {
  fixed = 'FIXED',
  variable = 'VARIABLE'
}

export enum Currency {
  dollars = 'DOLLARS',
  points = 'POINTS'
}

export interface AccountSummaryResponse {
  accountInfo: {
    accountLast4: string
  }
  profile: {
    'cipher.firstName': string
  }
  product: {
    portfolio: string
    type: ProductTypes
    lineOfBusiness?: LineOfBusiness
    displayName: string
    clientId?: string
    portfolioLogo: string
  }
  card: {
    cardArtURL?: string
  }
  billing: {
    autopayEnrollment?: boolean
    lastPaymentAmount?: string
    lastPaymentDate?: string
    nextPaymentDate?: string
    pastDue?: boolean
    pastDueAmount?: string
    statementBalance?: string
    statementEnrollment: StatementEnrollment
    daysDelinquent?: string
    customerAssistancePlan?: boolean
    settlementEnrolled?: boolean
    fixedPaymentAmount?: string
    interestPaid?: string
    totalMinimumPaymentDue?: string
  }
  credit: {
    annualPercentageRate?: string
    annualPercentageRateType?: AnnualPercentageRateType
    availableCredit: string
    creditLimit: string
    currentBalance: string
    temporaryCreditLimit?: string
  }
  installment: {
    principalAmount?: string
    monthlyAmountDue?: string
    totalAmountPaid?: string
    endDate?: string
    interestRate?: string
    loanTerm?: string
    payoffValidThrough?: string
    startDate?: string
    purchaseAmount?: string
    payOffValidThrough?: string
    payOffAmount?: string
    termRemaining?: number
    principalRemaining?: string
  }
  rewards: {
    currency?: Currency
    rewardsBalance?: string
  }
  servicing: {
    allowable?: boolean
    reason?: string
  }
  conditions: AccountSummaryConditions[]
}

export interface GetAccountSummaryRequest {
  accountId: string
  accountIdType: string
  accessToken: string
}
